import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Headphones, MapPin, Calendar, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { RouteCoverMedia } from '@/components/RouteCoverMedia';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RouteCard = ({ route, onOpen }) => {
  const cover = route.cover_photo_url ? `${BACKEND_URL}${route.cover_photo_url}` : null;
  const video = route.cover_video_url ? `${BACKEND_URL}${route.cover_video_url}` : null;
  const audios = route.audio_guide_count || 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`routes-spotlight-card-${route.share_id}`}
      className="group relative shrink-0 w-[58vw] sm:w-[220px] lg:w-[240px] aspect-[4/5] rounded-2xl overflow-hidden text-left bg-stone-200 ring-1 ring-stone-200 shadow-sm snap-start focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 transition-transform duration-300 hover:-translate-y-1"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-teal-700 via-teal-900 to-stone-900">
        {!cover && !video && (
          <Headphones className="absolute right-6 top-1/2 -translate-y-1/2 w-28 h-28 text-teal-400/30" aria-hidden="true" />
        )}
        <RouteCoverMedia
          videoUrl={video}
          coverUrl={cover}
          alt={route.title}
          imgClassName="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/25 to-stone-950/10" />
      <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 text-stone-900 text-[11px] font-bold backdrop-blur">
          <Calendar className="w-3 h-3" /> {route.duration_days || 1} {route.duration_days === 1 ? 'día' : 'días'}
        </span>
        {audios > 0 && (
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-teal-500 text-white text-[11px] font-bold shadow-lg"
            data-testid={`routes-spotlight-audio-${route.share_id}`}
          >
            <Headphones className="w-3 h-3" /> {audios} audioguía{audios === 1 ? '' : 's'}
          </span>
        )}
      </div>
      {video && (
        <span className="absolute top-14 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/40 text-white text-[10px] font-semibold backdrop-blur">
          <Play className="w-2.5 h-2.5 fill-white" /> vídeo
        </span>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-3.5 sm:p-4">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-teal-300 mb-1 inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {route.destination || route.country}
        </p>
        <h3
          className="text-white text-base sm:text-lg leading-tight mb-1.5 drop-shadow"
          style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700 }}
        >
          {route.title}
        </h3>
        <p className="text-white/75 text-xs">
          {route.places_count || 0} paradas{route.owner_name ? ` · por ${route.owner_name}` : ''}
        </p>
      </div>
    </button>
  );
};

export const RoutesSpotlight = () => {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/showcase/routes/featured?limit=8`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => {
        if (cancelled) return;
        const items = Array.isArray(d?.items) ? d.items : [];
        // Rutas con audioguía primero: es el argumento de esta temporada.
        items.sort((a, b) => (b.audio_guide_count || 0) - (a.audio_guide_count || 0));
        setRoutes(items);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.8, 680), behavior: 'smooth' });
  };

  if (routes.length === 0) return null;
  const totalAudios = routes.reduce((n, r) => n + (r.audio_guide_count || 0), 0);

  return (
    <section
      id="rutas"
      data-testid="routes-spotlight"
      className="py-16 lg:py-20 bg-white border-t border-stone-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 lg:mb-8">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-50 ring-1 ring-teal-200 text-teal-700 text-xs font-semibold mb-3">
              <Headphones className="w-3.5 h-3.5" /> Rutas con audioguía
              {totalAudios > 0 && <span className="text-teal-600/80">· {totalAudios} audios</span>}
            </span>
            <h2
              className="text-stone-900 leading-[1.08] mb-2"
              style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 700, fontSize: 'clamp(1.6rem, 3vw, 2.25rem)' }}
            >
              Rutas listas para copiar a tu viaje
            </h2>
            <p className="text-sm sm:text-base text-stone-600 leading-relaxed">
              Itinerarios reales con cada parada en el mapa. Cópialos en un clic y TOVÍAS te lo cuenta al llegar.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex gap-2">
              <button
                type="button"
                onClick={() => scrollBy(-1)}
                aria-label="Anterior"
                data-testid="routes-spotlight-prev"
                className="w-9 h-9 rounded-full ring-1 ring-stone-200 text-stone-600 hover:bg-stone-50 flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollBy(1)}
                aria-label="Siguiente"
                data-testid="routes-spotlight-next"
                className="w-9 h-9 rounded-full ring-1 ring-stone-200 text-stone-600 hover:bg-stone-50 flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <Link
              to="/rutas"
              data-testid="routes-spotlight-see-all"
              className="inline-flex items-center gap-2 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm px-4 py-2.5 transition-colors whitespace-nowrap"
            >
              Ver todas las rutas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div
          ref={scrollerRef}
          data-testid="routes-spotlight-scroller"
          className="flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {routes.map((r) => (
            <RouteCard key={r.share_id} route={r} onOpen={() => navigate(`/r/${r.share_id}`)} />
          ))}
          <Link
            to="/rutas?audio=1"
            data-testid="routes-spotlight-more-card"
            className="group shrink-0 w-[58vw] sm:w-[220px] lg:w-[240px] aspect-[4/5] rounded-2xl ring-1 ring-dashed ring-stone-300 hover:ring-teal-400 hover:bg-teal-50/40 snap-start flex flex-col items-center justify-center text-center p-5 transition-colors"
          >
            <span className="w-12 h-12 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Headphones className="w-5 h-5" />
            </span>
            <span className="font-bold text-stone-900">Explorar el catálogo</span>
            <span className="text-stone-500 text-xs mt-1">Filtra por país, días y audioguía</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default RoutesSpotlight;
